/**
 * Automatic canvas layout with ELK's layered algorithm (loaded on demand, about 400 kB).
 *
 * Bands keep the picture readable whatever the edges say: public services first, then everything
 * they call, then databases and volumes. Groups become compound nodes, so members stay together
 * inside their frame; a large group is packed into a block, and frames are placed by the edges between them.
 */
import type { ElkExtendedEdge, ElkNode } from "elkjs/lib/elk-api";
import type { CanvasDirection, GraphNode } from "@/lib/api";
import type { ServiceNodeData } from "./model";

export type Band = 0 | 1 | 2;

export interface LayoutItem {
  id: string;
  band: Band;
  width: number;
  height: number;
  /** Expanded group this item sits in. */
  group?: string;
  /** Sort key so equal-looking graphs always come out the same. */
  order: string;
}

export interface Point {
  x: number;
  y: number;
}

/** Space around members inside a group frame; the top leaves room for the label. */
export const GROUP_PAD = 20;
export const GROUP_HEAD = 44;
/** Groups with more members than this are packed into a block rather than laid out along their edges. */
const PACK_AT = 6;

const elkDirection: Record<CanvasDirection, string> = { LR: "RIGHT", TB: "DOWN" };

type Elk = { layout: (graph: ElkNode) => Promise<ElkNode> };
let elkPromise: Promise<Elk> | null = null;

/** Start downloading ELK early (the route loader calls this) so Tidy up is instant. */
export function loadElk(): Promise<Elk> {
  elkPromise ??= import("elkjs/lib/elk.bundled.js").then((m) => new m.default() as unknown as Elk);
  return elkPromise;
}

/** Canvas key (KIND.NAME) for a graph node: services.web, cron.nightly-report, databases.main, volumes.uploads. */
export function refOf(n: Pick<GraphNode, "type" | "name">): string {
  return n.type === "database" ? `databases.${n.name}` : n.type === "volume" ? `volumes.${n.name}` : n.type === "cron" ? `cron.${n.name}` : `services.${n.name}`;
}

/** Public web services that nothing else calls sit in the first band; data sits in the last. */
export function bandsOf(nodes: GraphNode[], edges: { from: string; to: string; kind: string }[]): Record<string, Band> {
  const called = new Set(edges.filter((e) => e.kind === "network").map((e) => e.to));
  const out: Record<string, Band> = {};
  for (const n of nodes) {
    if (n.type === "database" || n.type === "volume") out[n.id] = 2;
    else if ((n.data as ServiceNodeData | undefined)?.kind === "web" && !called.has(n.id)) out[n.id] = 0;
    else out[n.id] = 1;
  }
  return out;
}

/**
 * Lay out `items` and return each one's top-left corner. Items in an expanded group are laid out
 * inside a compound node and come back in absolute coordinates.
 */
export async function layeredLayout(items: LayoutItem[], edges: { id: string; from: string; to: string }[], direction: CanvasDirection): Promise<Record<string, Point>> {
  const elk = await loadElk();
  const horizontal = direction === "LR";
  const sorted = [...items].sort((a, b) => a.band - b.band || a.order.localeCompare(b.order));
  const leaf = (i: LayoutItem): ElkNode => ({ id: i.id, width: i.width, height: i.height, layoutOptions: { "elk.partitioning.partition": String(i.band) } });

  const known = new Set(items.map((i) => i.id));
  const groupOf = new Map(items.filter((i) => i.group).map((i) => [i.id, i.group as string]));
  /** Top-level node an item is drawn in: its group's frame, or itself. */
  const topOf = (id: string) => (groupOf.has(id) ? `frame:${groupOf.get(id)}` : id);
  const inner = new Map<string, ElkExtendedEdge[]>();
  const outer: ElkExtendedEdge[] = [];
  const seen = new Set<string>();
  for (const e of edges) {
    if (!known.has(e.from) || !known.has(e.to) || e.from === e.to) continue;
    const g = groupOf.get(e.from);
    // An edge inside a group is laid out with the group; one between groups only places the frames.
    const [from, to] = g && g === groupOf.get(e.to) ? [e.from, e.to] : [topOf(e.from), topOf(e.to)];
    const key = `${from}->${to}`;
    if (from === to || seen.has(key)) continue;
    seen.add(key);
    const edge = { id: `${e.id}|${key}`, sources: [from], targets: [to] };
    if (g && g === groupOf.get(e.to)) inner.set(g, [...(inner.get(g) ?? []), edge]);
    else outer.push(edge);
  }

  const children: ElkNode[] = [];
  const placed = new Set<string>();
  for (const i of sorted) {
    if (placed.has(i.id)) continue;
    if (i.group) {
      const members = sorted.filter((m) => m.group === i.group);
      members.forEach((m) => placed.add(m.id));
      // A big group is packed into a block in name order: as a layered row it would be dozens of cards wide.
      const packed = members.length > PACK_AT;
      children.push({
        id: `frame:${i.group}`,
        layoutOptions: {
          "elk.padding": `[top=${GROUP_HEAD},left=${GROUP_PAD},bottom=${GROUP_PAD},right=${GROUP_PAD}]`,
          "elk.partitioning.partition": String(Math.min(...members.map((m) => m.band))),
          "elk.spacing.nodeNode": "32",
          ...(packed
            ? { "elk.algorithm": "rectpacking", "elk.aspectRatio": "1.4", "elk.rectpacking.trybox": "false" }
            : {
                "elk.algorithm": "layered",
                "elk.partitioning.activate": "true",
                "elk.direction": elkDirection[direction],
                "elk.layered.spacing.nodeNodeBetweenLayers": horizontal ? "96" : "72",
              }),
        },
        children: members.map(leaf),
        edges: packed ? [] : (inner.get(i.group) ?? []),
      });
    } else {
      placed.add(i.id);
      children.push(leaf(i));
    }
  }

  const root = await elk.layout({
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": elkDirection[direction],
      // Each frame is laid out on its own first, then placed as one box by the edges between frames.
      "elk.hierarchyHandling": "SEPARATE_CHILDREN",
      "elk.partitioning.activate": "true",
      // One component, so bands hold for things with no connections too (a lone cron job sits with the services).
      "elk.separateConnectedComponents": "false",
      "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
      "elk.spacing.nodeNode": horizontal ? "44" : "72",
      "elk.layered.spacing.nodeNodeBetweenLayers": horizontal ? "120" : "88",
      "elk.spacing.edgeNode": horizontal ? "24" : "40",
      "elk.padding": "[top=0,left=0,bottom=0,right=0]",
    },
    children,
    edges: outer,
  });

  if (groupOf.size) await tierFrames(elk, root, children, direction);

  const out: Record<string, Point> = {};
  const walk = (n: ElkNode, ox: number, oy: number) => {
    for (const c of n.children ?? []) {
      const x = ox + (c.x ?? 0);
      const y = oy + (c.y ?? 0);
      if (c.children?.length) walk(c, x, y);
      else out[c.id] = { x: Math.round(x), y: Math.round(y) };
    }
  };
  walk(root, 0, 0);
  return out;
}

/**
 * Re-place the top-level boxes of a grouped layout in three tiers: the front (band 0) in one line, everything in
 * between packed into a block in the order the layered pass found (callers before what they call), and data in
 * one line at the end. Edges between groups make the layered pass a long chain of frames, one per layer.
 */
async function tierFrames(elk: Elk, root: ElkNode, children: ElkNode[], direction: CanvasDirection) {
  const horizontal = direction === "LR";
  const gap = horizontal ? 120 : 88;
  const bandOf = (n: ElkNode) => Number(n.layoutOptions?.["elk.partitioning.partition"] ?? 1);
  const boxes = (root.children ?? []).map((n) => ({ n, band: bandOf(children.find((c) => c.id === n.id) ?? n) }));
  // Along the flow first, then across it: the layered pass's reading order.
  const along = (n: ElkNode) => (horizontal ? (n.x ?? 0) : (n.y ?? 0));
  const across = (n: ElkNode) => (horizontal ? (n.y ?? 0) : (n.x ?? 0));
  const tiers = [0, 1, 2].map((b) =>
    boxes
      .filter((x) => x.band === b)
      .map((x) => x.n)
      .sort((a, b) => along(a) - along(b) || across(a) - across(b)),
  );
  let offset = 0;
  for (const [t, tier] of tiers.entries()) {
    if (!tier.length) continue;
    let extent = 0;
    if (t === 1 && tier.length > 1) {
      const packed = await elk.layout({
        id: "tier",
        layoutOptions: { "elk.algorithm": "rectpacking", "elk.aspectRatio": horizontal ? "1" : "2", "elk.spacing.nodeNode": String(gap), "elk.padding": "[top=0,left=0,bottom=0,right=0]" },
        children: tier.map((n) => ({ id: n.id, width: n.width, height: n.height })),
      });
      for (const p of packed.children ?? []) {
        const n = tier.find((x) => x.id === p.id)!;
        // rectpacking fills rows; for a left-to-right flow its rows become columns.
        const [a, c] = horizontal ? [p.y ?? 0, p.x ?? 0] : [p.x ?? 0, p.y ?? 0];
        n.x = horizontal ? offset + c : a;
        n.y = horizontal ? a : offset + c;
        extent = Math.max(extent, c + (horizontal ? (n.width ?? 0) : (n.height ?? 0)));
      }
    } else {
      let pos = 0;
      for (const n of tier) {
        if (horizontal) [n.x, n.y] = [offset, pos];
        else [n.x, n.y] = [pos, offset];
        pos += (horizontal ? (n.height ?? 0) : (n.width ?? 0)) + gap;
        extent = Math.max(extent, horizontal ? (n.width ?? 0) : (n.height ?? 0));
      }
    }
    offset += extent + gap;
  }
}

/** Move `p` down (or right, for vertical flows) until it no longer overlaps any of `taken`. */
export function nudgeClear(p: Point, size: { width: number; height: number }, taken: { x: number; y: number; width: number; height: number }[], direction: CanvasDirection): Point {
  const gap = 32;
  const q = { ...p };
  for (let guard = 0; guard < 200; guard++) {
    const hit = taken.find((t) => q.x < t.x + t.width + gap && q.x + size.width + gap > t.x && q.y < t.y + t.height + gap && q.y + size.height + gap > t.y);
    if (!hit) return q;
    if (direction === "TB") q.x = hit.x + hit.width + gap;
    else q.y = hit.y + hit.height + gap;
  }
  return q;
}

/** Ease out cubic. */
export const ease = (t: number) => 1 - Math.pow(1 - t, 3);

export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}
